import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Paper, Divider, CircularProgress, Link,
} from '@mui/material';
import {
  Phone as LlamadaIcon,
  Sms as SmsIcon,
  CarCrash as AccidenteIcon,
  Fence as ZonaIcon,
  Shield as SeguridadIcon,
  FiberManualRecord as PuntoIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// El detalle de un evento del worker, en cristiano.
//
// El JSON tiene todo pero no se lee: para saber si una llamada entró hay que ir buscando
// `reachedHuman` entre quince claves. Aquí arriba va lo que pasó en una línea, y debajo la
// secuencia completa del mismo suceso —llamada, respuesta, SMS— que es lo que de verdad
// contesta "¿avisamos a alguien cuando este carro chocó?".
//
// El JSON sigue abajo, entero: cuando algo no cuadra, es lo único que no miente.
// ─────────────────────────────────────────────────────────────────────────────

interface Log {
  _id: string;
  timestamp: string;
  level: string;
  category: string;
  action: string;
  userId?: number;
  deviceId?: number;
  eventId?: number;
  sourceIp?: string;
  details?: any;
  veces?: number;
  primeraVez?: string;
  sourceIps?: string[];
  motivo?: string;
}

const hora = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('es-DO', { timeZone: 'America/Santo_Domingo', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '—';

const soloHora = (iso?: string) =>
  iso ? new Date(iso).toLocaleTimeString('es-DO', { timeZone: 'America/Santo_Domingo', hour12: false }) : '—';

// Los motivos por los que el worker decide no apagar un vehículo, dichos como se los diría
// una persona a otra.
const MOTIVOS: Record<string, string> = {
  autoShutdown_disabled: 'el cliente tiene el apagado automático desactivado',
  not_premium: 'el plan no cubre esta función',
  speed_check_failed: 'el vehículo iba demasiado rápido para apagarlo',
  no_subscription: 'no tiene suscripción activa',
  missing_secret: 'la petición llegó sin la clave',
  wrong_secret: 'la petición llegó con una clave equivocada',
  signature_mismatch: 'la firma no coincide',
};
const motivoLegible = (m?: string) => (m ? MOTIVOS[m] || m.replace(/_/g, ' ') : null);

const ESTADO_LLAMADA: Record<string, string> = {
  completed: 'contestada',
  'no-answer': 'no contestaron',
  busy: 'ocupado',
  failed: 'falló',
  canceled: 'cancelada',
};

const ICONO: Record<string, React.ReactNode> = {
  sinch: <LlamadaIcon fontSize="small" />,
  accident: <AccidenteIcon fontSize="small" />,
  safezone: <ZonaIcon fontSize="small" />,
  security: <SeguridadIcon fontSize="small" />,
  error: <SmsIcon fontSize="small" />,
};

/** Una frase que resume el evento, y las etiquetas de estado que lo acompañan. */
function resumir(log: Log): { frase: string; chips: Array<{ t: string; c: 'success' | 'error' | 'warning' | 'default' }> } {
  const d = log.details || {};
  const chips: Array<{ t: string; c: 'success' | 'error' | 'warning' | 'default' }> = [];

  switch (log.action) {
    case 'call_dispatched': {
      chips.push(d.dispatchSuccess ? { t: 'salió', c: 'success' } : { t: 'no salió', c: 'error' });
      if (d.providerAttempts > 1) chips.push({ t: `${d.providerAttempts} intentos`, c: 'warning' });
      if (d.estimatedCostUSD) chips.push({ t: `$${d.estimatedCostUSD}`, c: 'default' });
      if (d.twilioErrorMessage) chips.push({ t: d.twilioErrorMessage, c: 'error' });
      return { frase: `Llamada a ${d.contactName || 'contacto'} ${d.phoneLast4 || ''}`, chips };
    }
    case 'call_status_callback': {
      const estado = ESTADO_LLAMADA[d.callStatus] || d.callStatus || 'sin estado';
      chips.push({ t: estado, c: d.callStatus === 'completed' ? 'success' : 'warning' });
      if (d.reachedHuman) chips.push({ t: 'habló con una persona', c: 'success' });
      if (d.durationSec != null) chips.push({ t: `${d.durationSec} s`, c: 'default' });
      return { frase: 'Respuesta de la llamada', chips };
    }
    case 'sms_dispatched': {
      chips.push(d.success ? { t: 'enviado', c: 'success' } : { t: 'no se envió', c: 'error' });
      if (d.errorMessage) chips.push({ t: d.errorMessage, c: 'error' });
      return { frase: `SMS a ${d.contactName || 'contacto'} ${d.phoneLast4 || ''}`, chips };
    }
    case 'processed': {
      chips.push(d.isPremium ? { t: 'con plan', c: 'success' } : { t: 'sin plan que lo cubra', c: 'warning' });
      if (d.actionTaken) chips.push({ t: d.actionTaken === 'LOGGED_ONLY' ? 'solo se registró' : d.actionTaken, c: d.actionTaken === 'LOGGED_ONLY' ? 'warning' : 'success' });
      if (d.requiresManualReview) chips.push({ t: 'pendiente de revisar', c: 'warning' });
      if (d.position?.speedKmh != null) chips.push({ t: `${Math.round(d.position.speedKmh)} km/h`, c: 'default' });
      return { frase: `Accidente de ${d.deviceName || 'un equipo'}`, chips };
    }
    case 'engine_stop_sent': {
      chips.push(d.traccarSuccess ? { t: 'orden enviada', c: 'success' } : { t: 'falló el envío', c: 'error' });
      if (d.position?.speedKmh != null) chips.push({ t: `${Math.round(d.position.speedKmh)} km/h`, c: 'default' });
      return { frase: `Apagado remoto · zona ${d.geofenceName || d.geofenceId || ''}`, chips };
    }
    case 'engine_stop_skipped': {
      chips.push({ t: 'no se apagó', c: 'warning' });
      const m = motivoLegible(d.skipReason);
      if (m) chips.push({ t: m, c: 'default' });
      return { frase: `Salió de la zona ${d.geofenceName || d.geofenceId || ''}`, chips };
    }
    default: {
      if (log.level === 'security') {
        if (log.veces && log.veces > 1) chips.push({ t: `${log.veces} veces`, c: 'warning' });
        if (log.sourceIps?.length) chips.push({ t: `${log.sourceIps.length} IP(s)`, c: 'default' });
        const m = motivoLegible(log.motivo || log.details?.reason);
        if (m) chips.push({ t: m, c: 'default' });
        return { frase: 'Intento de entrar sin permiso', chips };
      }
      const m = motivoLegible(log.details?.reason || log.details?.skipReason);
      if (m) chips.push({ t: m, c: 'default' });
      return { frase: log.action.replace(/_/g, ' '), chips };
    }
  }
}

const DetalleEventoWorker: React.FC<{ log: Log }> = ({ log }) => {
  const [secuencia, setSecuencia] = useState<Log[] | null>(null);
  const [cargando, setCargando] = useState(false);

  const cargarSecuencia = useCallback(async () => {
    if (!log.eventId) return;
    setCargando(true);
    try {
      const res = await apiService.getAuditLogs({ eventId: log.eventId, limit: 50 });
      const lista: Log[] = (res?.data || []).slice().sort(
        (a: Log, b: Log) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      setSecuencia(lista);
    } catch {
      setSecuencia([]);
    } finally {
      setCargando(false);
    }
  }, [log.eventId]);

  useEffect(() => { cargarSecuencia(); }, [cargarSecuencia]);

  const { frase, chips } = resumir(log);
  const d = log.details || {};
  const posicion = d.position;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Qué pasó, en una línea */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {ICONO[log.category] || <PuntoIcon fontSize="small" />}
        <Typography variant="subtitle2" fontWeight={600}>{frase}</Typography>
        {chips.map((c, i) => (
          <Chip key={i} size="small" label={c.t} color={c.c} variant={c.c === 'default' ? 'outlined' : 'filled'}
            sx={{ height: 20, fontSize: '0.7rem', maxWidth: 380 }} />
        ))}
      </Box>

      {/* Dónde fue */}
      {posicion?.lat != null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {d.address || posicion.address || `${posicion.lat}, ${posicion.lon}`}{' · '}
          <Link href={`https://www.google.com/maps/search/?api=1&query=${posicion.lat},${posicion.lon}`}
            target="_blank" rel="noopener noreferrer">ver en el mapa</Link>
        </Typography>
      )}

      {/* Los intentos agrupados: desde cuándo y desde dónde */}
      {log.level === 'security' && log.veces && log.veces > 1 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Desde {hora(log.primeraVez)} hasta {hora(log.timestamp)}
          {log.sourceIps?.length ? ` · IPs: ${log.sourceIps.slice(0, 6).join(', ')}${log.sourceIps.length > 6 ? '…' : ''}` : ''}
        </Typography>
      )}

      {/* La secuencia completa del mismo suceso */}
      {log.eventId != null && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Todo lo que pasó con este suceso (evento {log.eventId})
          </Typography>
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} /></Box>
          ) : !secuencia?.length ? (
            <Typography variant="body2" color="text.secondary">No hay más registros de este suceso.</Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              {secuencia.map((p, i) => {
                const r = resumir(p);
                const actual = p._id === log._id;
                return (
                  <Box key={p._id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.4 }}>
                      <PuntoIcon sx={{ fontSize: 10, color: actual ? 'primary.main' : 'text.disabled' }} />
                      {i < secuencia.length - 1 && <Box sx={{ width: '1px', flexGrow: 1, minHeight: 16, backgroundColor: 'divider' }} />}
                    </Box>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" color="text.secondary" sx={{ minWidth: 68 }}>
                      {soloHora(p.timestamp)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={actual ? 600 : 400}>{r.frase}</Typography>
                      {r.chips.slice(0, 3).map((c, j) => (
                        <Chip key={j} size="small" label={c.t} color={c.c} variant="outlined"
                          sx={{ height: 18, fontSize: '0.68rem', maxWidth: 280 }} />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      )}

      <Divider sx={{ my: 1 }} />
    </Box>
  );
};

export default DetalleEventoWorker;
